import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { useActiveTenant } from '@/tenant/context'
import { formatDateTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, Loading } from '@/ui/states'

/**
 * The organisation's own audit log — SUP-41, and the screen the support banner
 * had been promising since S-B11.
 *
 * **This is the panel that did not exist.** `SupportAccessBanner` told customers
 * that a support session was *"recorded in your organisation's audit log"*, and
 * screen 08's own card — *"What the tenant sees in their own audit log"*, ending
 * *"there is no silent mode"* — is a promise about a screen. The entries were
 * always real: `impersonation.start` and `impersonation.end` are written into the
 * tenant's hash-chained log inside the same transaction as the grant, and
 * `GET /api/v1/audit` has served them since S-B12. What was missing was anywhere
 * to read them, so the banner named a record with no route to it and the owner
 * went looking for one on the first live support session and found nothing.
 *
 * **The staff console's screen 10 reads the same rows.** That is the property
 * screen 10 states on its own face — *"there is no internal-only view"* — and it
 * is only true from this side once this panel exists. Before it, "the same rows
 * the customer sees" described a view the customer could not open.
 *
 * WHAT IT SHOWS, AND WHAT IT DELIBERATELY DOES NOT
 * ------------------------------------------------
 * Time, who, what, and why — the four columns that let somebody check a claim.
 * `before` and `after` are **not** rendered, although the API returns them to a
 * caller with `audit:read`: they hold the values a change moved between, they are
 * shaped for a machine, and a settings panel that printed raw JSON diffs would be
 * a debugging view wearing a customer screen's clothes. The reason field is what
 * this panel is for, and it is the one thing the staff side cannot write an entry
 * without.
 *
 * **`actor_kind` is the fact that matters most here**, so it leads each row as a
 * badge rather than being a column somebody has to look across for: the question
 * a customer opens this panel with is *"did AugRank touch my account"*, and
 * `staff` is the answer or it is not.
 *
 * **Paging is `before_sequence`, not a page number.** The log is append-only and
 * grows at the head, so a numbered page would shift under the reader every time
 * anybody in the organisation did anything. `sequence_number` is the entry's own
 * position in this tenant's chain and is strictly decreasing down the list, which
 * makes "everything before this one" exact. The panel keeps a stack of the
 * cursors it has visited so *Newer* is the page actually seen rather than a
 * recomputed guess — `admin/src/pages/Audit.tsx` makes the same argument at
 * length for the same reason.
 */
/**
 * `AuditEntryOut`, generated — never hand-written.
 *
 * This was a hand-written object literal naming seven of the response's thirteen
 * fields, which is the one thing the stack rules forbid outright: a DTO declared
 * in TypeScript drifts from the server silently and defeats the reason FastAPI
 * was chosen. It was already wrong in one direction — `actor_label` was declared
 * optional where the schema has it nullable-but-present — and a rename on the API
 * side would have compiled clean here and rendered `undefined`.
 */
type Entry = Schema<'AuditEntryOut'>

/**
 * How each actor kind is named for the customer.
 *
 * *"AugRank staff"* rather than "Staff", because on the customer's own screen the
 * bare word is ambiguous — their own team are staff too. That ambiguity is the
 * whole point of the row: this panel exists so somebody can tell an act of theirs
 * from an act of ours.
 */
const ACTORS: Readonly<Record<string, { label: string; tone: string }>> = {
  user: { label: 'Your team', tone: 'ar-badge--neutral' },
  staff: { label: 'Support staff', tone: 'ar-badge--danger' },
  system: { label: 'Automatic', tone: 'ar-badge--info' },
}

/**
 * `subscription.changed` as *"Subscription changed"*.
 *
 * The action vocabulary is `app/audit/actions.py` — dotted, lower-case, stable,
 * and written for a machine. Rendering it raw on a customer's screen would make
 * them read an identifier; rendering a hand-written lookup of the sixty-odd
 * values would be a table that silently stops covering the log the first time a
 * session adds an action to it. Splitting on the separators is neither: a new
 * action reads correctly on the day it is added.
 */
function actionLabel(action: string): string {
  const words = action
    .split(/[._]/)
    // The initialisms in the vocabulary, kept as initialisms: *SEO publish
    // configured*, not *Seo publish configured* (walkthrough 2026-09-12).
    .map((word) => INITIALISMS[word] ?? word)
    .join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const INITIALISMS: Record<string, string> = {
  seo: 'SEO',
  linkedin: 'LinkedIn',
  ga4: 'GA4',
  dns: 'DNS',
  gstin: 'GSTIN',
  dpdp: 'DPDP',
  oauth: 'OAuth',
}

export function AuditPanel(): JSX.Element {
  const { tenant } = useActiveTenant()
  const [before, setBefore] = useState<number | null>(null)
  /** The cursors of the pages already visited. See the docstring. */
  const [trail, setTrail] = useState<readonly (number | null)[]>([])

  const log = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/audit', {
          signal,
          params: { query: { ...(before === null ? {} : { before_sequence: before }) } },
        }),
      ),
    [before],
  )

  if (log.state.status === 'loading') return <Loading label="Reading your audit log" />
  if (log.state.status === 'error') {
    return <ErrorState failure={log.state.failure} onRetry={log.refetch} />
  }

  const entries: readonly Entry[] = log.state.data.entries
  // The server sends the last entry's sequence number on every page, including
  // the last one. "There is more" is that number minus the page: an entry
  // numbered 1 is the first this organisation ever wrote, so there is nothing
  // before it.
  // The cursor for the page *before* this one, or `null` at the beginning of the
  // log. `sequence_number` is the entry's position in this tenant's own chain, so
  // an entry numbered 1 is the first they ever wrote and there is nothing before
  // it to ask for. One value rather than a row plus a predicate, because the
  // button's `disabled` and its `onClick` must not be able to disagree.
  const oldest = entries.length > 0 ? entries[entries.length - 1] : undefined
  const olderCursor =
    oldest !== undefined && oldest.sequence_number > 1 ? oldest.sequence_number : null

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Audit log</h2>
      </div>

      <div className="ar-card__body">
        <div className="ar-alert ar-alert--info">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>Everything is here, including us</strong>
            Entries cannot be edited or deleted by anyone, including support. If a member of our
            support team opens your account, the start and the end are written here with the reason
            they gave — <strong>there is no silent mode</strong>.
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <Empty
          icon="◷"
          title="Nothing recorded yet"
          body="The log fills as soon as anybody does anything — a member invited, a setting changed, an article approved."
        />
      ) : (
        <ul className="app-audit">
          {entries.map((entry) => {
            const actor = ACTORS[entry.actor_kind] ?? {
              label: entry.actor_kind,
              tone: 'ar-badge--neutral',
            }
            return (
              <li className="app-audit__row" key={entry.sequence_number}>
                <span className={`ar-badge ${actor.tone}`}>{actor.label}</span>
                <div className="app-audit__what">
                  <strong>{actionLabel(entry.action)}</strong>
                  {entry.reason ? (
                    <span className="app-audit__reason">{entry.reason}</span>
                  ) : entry.entity_type ? (
                    <span className="app-audit__reason">{entry.entity_type}</span>
                  ) : null}
                </div>
                <span className="app-audit__who">{entry.actor_label ?? '—'}</span>
                <span className="app-audit__when">
                  {formatDateTime(entry.occurred_at, tenant.timezone)}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <div className="ar-card__footer">
        <span className="ar-subtle">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          {olderCursor === null ? ', this is the beginning of your log' : ', more before this page'}
        </span>
        <span className="ar-card__spacer" />
        <Button
          size="sm"
          disabled={trail.length === 0}
          onClick={() => {
            const previous = trail[trail.length - 1] ?? null
            setTrail(trail.slice(0, -1))
            setBefore(previous)
          }}
        >
          Newer
        </Button>
        <Button
          size="sm"
          disabled={olderCursor === null}
          onClick={() => {
            if (olderCursor === null) return
            setTrail([...trail, before])
            setBefore(olderCursor)
          }}
        >
          Older
        </Button>
      </div>
    </section>
  )
}
